/**
 * A section's questions, rendered from data — the body of every section screen.
 *
 * This is the connected half of the pair: `QuestionField` renders a control,
 * this decides which controls exist, what the chips under them say, and where
 * an answer is written. It reads all four of those from the engines and
 * composes nothing of its own:
 *
 *   which questions   ← `visibleQuestions(section)` → `isVisible()`
 *   which options     ← `optionsFor(q, doc, scope)` → D-007 + D-024
 *   the L4a ceiling   ← `derivedFor('L1')`          → `computeDerived()`
 *   the math chips    ← `issuesFor()`               → `validate()`
 *   the shunt flip    ← `ValidationIssue.autoFix`   → §4 rule 5
 *
 * `optionsFor` is why the option narrowing is done HERE and handed down rather
 * than left to the renderer: a per-option gate (**D-024** — F8's Shunt relay
 * box exists only in a sprinklered machine room) has to be evaluated against
 * the document, and `QuestionField` deliberately has no document. This is the
 * component that does, so this is the component that asks.
 *
 * ── The FLS block (Amendment 5) ──────────────────────────────────────────────
 * §3.11's questions are filed in the section that repeats at each one's own
 * entity level and ordered after everything else in it, so they arrive here
 * already contiguous at the end of the list. What this adds is the header in
 * front of the first of them — `FlsBlock`, which carries the fire alarm
 * company's photo prompts — so five scattered questions read as one errand.
 *
 * ── Live validation ──────────────────────────────────────────────────────────
 * The chip text is `ValidationIssue.message`, verbatim, and it updates on every
 * tap because `SurveyProvider` re-runs the engines on every persisted write.
 * "Mainline disconnects: 4 + 2 = 6 — …" is the engine's sentence, not one
 * assembled here; a second implementation of §4 rule 2 in a `.tsx` is precisely
 * how the form and the estimate drift apart.
 *
 * An issue that names several questions (V2 names E1a AND E1b) is rendered ONCE,
 * under the first of them on screen — the mockup shows one chip under the pair,
 * and two copies of the same sentence is noise on a 430px screen.
 *
 * `MathChip` is an `aria-live` region, so the changed count is announced without
 * the surveyor hunting for it.
 *
 * ── Branch reveals ───────────────────────────────────────────────────────────
 * A question that has just become visible (M4 → the E4 shunt block, E7 → the
 * conduit legs) animates open instead of snapping into place, so it is obvious
 * that an answer opened it rather than that the list jumped. Tracked by
 * comparing the visible ids against the previous render; questions that go away
 * are forgotten, so toggling a gate back and forth animates every time.
 * `prefers-reduced-motion` is honoured globally in theme.css.
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';

import type {
  AnswerValue,
  QuestionDef,
  Scope,
  SectionInstance,
  SurveyDocument,
  ValidationIssue,
} from '../../core/types';
import { DERIVED_ONLY_QUESTION_IDS } from '../../core/catalog';
import { optionsFor } from '../../core/validation';
import {
  isDefaultedAnswer,
  pendingDefaults,
  type PendingDefault,
} from '../state/answerDefaults';
import { formSetOf } from '../state/results';
import { useSurvey } from '../state/SurveyProvider';
import { heldForNow } from '../state/validationTiming';
import { MathChip, toneForSeverity, type MathTone } from './Chips';
import { FlsBlock } from './FlsBlock';
import { isFlsQuestion } from './flsShots';
import { NotesField, noteFor } from './NotesField';
import { PhotoCapture } from './PhotoCapture';
import { QuestionField } from './QuestionField';

/**
 * What a `guard` decided about one answer, before `QuestionList` acts on it.
 *
 * The guard's job used to be answered with a single boolean, and that boolean
 * was overloaded: `guardRoomCount`'s "nothing to lose" branch writes the
 * document itself (via `applyRoomCount`) and then returned `false` — meaning
 * only "QuestionList should not ALSO write this", not "nothing was written".
 * `QuestionList` could not tell the two apart, so a write that landed was
 * reported to the `Stepper` as refused, and a refused send that had actually
 * landed made the stepper forget a real, accepted number.
 *
 *   - `'write'`   — an ordinary answer. `QuestionList` persists it.
 *   - `'written'` — the guard persisted the change itself (a non-destructive
 *                   room drop, say). `QuestionList` writes nothing more.
 *   - `'refused'` — nothing was written. The guard is asking first.
 */
export type GuardVerdict = 'write' | 'written' | 'refused';

export interface QuestionListProps {
  section: SectionInstance;
  /**
   * Render no per-question work at all: no note field, no photo tiles, even
   * where they already exist.
   *
   * The LAST LOOK is the only caller, and the only one it should ever have.
   * That screen is the last thirty seconds of a day's work and the one thing it
   * must do is get the answer in; the note and the photos belong to the section
   * screen, one tap away, where the surveyor wrote them.
   *
   * Everywhere else this is a way to STRAND work. Since R13 took the ✎ off the
   * card, `QuestionWork` is the only surface a question's existing note or
   * photos have, so `bare` does not hide an icon — it hides the note itself,
   * with nothing left to reach it by. The Building screen passed it for one
   * round; B1–B17 are all INFO/AUTO so no icon was ever drawn there anyway,
   * which meant the flag changed exactly one thing: a note or a photo on a
   * building question had no surface in the app at all, and that screen carries
   * no section-level notes field to fall back to.
   */
  bare?: boolean;
  /**
   * Render only these question ids, in the section's own catalog order — the
   * last look's inline answering (**D-023**).
   *
   *   *"It sure would be nice if I could answer all the missing questions right
   *    here on the last 'Look Before You Send' screen."*
   *
   * The point of doing it through THIS component rather than a second renderer
   * is that there is then no second write path. The control, the scope
   * narrowing, the D-016 defaults and the persistence are the section screen's,
   * byte for byte; only the set of questions on screen is different. A second
   * implementation is how the last look and the section screen would start
   * disagreeing about the same answer.
   *
   * An id that is not visible in this section right now is simply absent —
   * `visibleQuestions` still decides what may be asked, and this never widens it.
   */
  only?: readonly string[];
  /**
   * Suppress the per-question validation chips.
   *
   * For the inline case only, and for one reason: the last look's ROW already
   * states the rule, its running total and its target, above both controls. A
   * chip under the first of them would be the same sentence twice on a 430px
   * screen, and §4 rules 2 and 3 are about the PAIR, not about either half.
   */
  chips?: boolean;
  /**
   * Intercept a write before it is persisted. See `GuardVerdict`: `'write'`
   * lets `QuestionList` persist the answer as usual, `'written'` says the
   * guard already did, and `'refused'` says nothing happened at all — the
   * caller has taken responsibility for that answer for now.
   *
   * One question needs this: B12, "How many machine rooms?". Room slots derive
   * from it, so lowering it below the rooms that already exist discards a room
   * survey, and that has to be asked about before it happens (D-014). The
   * control stays a plain stepper reading its value out of the document, so a
   * blocked write simply leaves the number where it was.
   */
  guard?: (questionId: string, next: AnswerValue) => GuardVerdict;
  /**
   * Every control on this section renders disabled — round 7 task 10, ruling
   * 12. Defaults to `useSurvey().readOnly`, so a submitted survey is
   * read-only everywhere `QuestionList` is the renderer without any caller
   * having to ask; a caller never has reason to pass `true` where the
   * document is not submitted, so this exists to be read, not overridden.
   */
  disabled?: boolean;
}

export function QuestionList({
  section,
  bare = false,
  guard,
  only,
  chips = true,
  disabled,
}: QuestionListProps): ReactNode {
  const {
    doc,
    results,
    readOnly,
    visibleQuestions,
    derivedFor,
    issuesFor,
    answer,
    setAnswer,
    applyFix,
    applyDefaults,
  } = useSurvey();
  const fieldsDisabled = disabled ?? readOnly;

  const asked = visibleQuestions(section);
  // Narrowed AFTER visibility, never instead of it: `only` may hide a question
  // the section asks, and may never reveal one it does not.
  const questions = only ? asked.filter((question) => only.includes(question.id)) : asked;
  // Building and ad hoc sections have no room, so no option is form-set tagged
  // and either value gives the same list.
  const formSet = formSetOf(doc, section.scope) ?? 'traction';
  const revealed = useRevealed(questions);

  /* ── D-016: nothing on this screen opens unanswered ───────────────────── */
  /*
   * …EXCEPT when this is the last look's inline view (D-023).
   *
   * Every row on that screen is there because an answer is OUTSTANDING. If
   * merely rendering the row wrote the form's own default into the document, the
   * gap would clear itself the instant the surveyor scrolled past it — a
   * question nobody ever saw, going to the office silently, which is the exact
   * defect D-016 and D-017 exist to stop, reintroduced by the fix for the one
   * after them. The last look asks; it never answers.
   *
   * (Today no `unanswered` gap can name a question that HAS a default — the
   * section screen wrote it when the surveyor walked through. This is the guard
   * that keeps that true rather than a lucky property of the current catalog.)
   */
  // Read-only: nothing is being asked here any more, so there is nothing to
  // open a default answer on either — and the write would only be refused
  // (ruling 12) and logged for no reason on every render of a sealed survey.
  useAnswerDefaults(
    doc,
    only || fieldsDisabled ? NO_QUESTIONS : questions,
    section.scope,
    applyDefaults,
  );

  /* ── §4 rule 5: apply the flip, then explain it ───────────────────────── */
  const sectionIssues = results.issuesBySection.get(section.id) ?? [];
  const flipIssue = sectionIssues.find(
    (issue) => issue.autoFix?.kind === 'shunt_refurb_to_replace',
  );
  /*
   * NOT in the inline view (D-023). `useShuntFlip` WRITES — it applies the §4
   * rule 5 auto-fix and then explains it in a red box. That is the section
   * screen's business, on the screen where the shunt questions were answered.
   * The last look asks; it never answers, and it certainly never rewrites an
   * answer the surveyor cannot see from there.
   */
  const flipNote = useShuntFlip(
    only || fieldsDisabled ? undefined : flipIssue,
    () => answer('E4c1', section.scope),
    applyFix,
  );
  // Anchor the red box to the last of the issue's questions that is on screen —
  // E4c1, the follow-up that triggered it. Data-driven, so a change to the
  // rule's question list moves the box without touching this component.
  const flipAnchor = flipAnchorFor(flipIssue, questions);

  /* ── one chip per issue, under the first question that owns it ─────────── */
  /*
   * The D-014 / D-015 gate, and it is not a filter on correctness: it withholds
   * a finding until the surveyor has been asked for everything it compares.
   *
   *   V1      the building total against the cars in every machine room — held
   *           until every declared room has its cars.
   *   V2/V3   a disconnect split against the room's car count — held until BOTH
   *           halves of the split carry an answer. Otherwise a six-car room's
   *           Electrical section opens at 0 + 0 ≠ 6 and is red before it has
   *           been touched.
   *
   * A held issue says NOTHING until it is due (D-029 R4 — the calm progress
   * sentences died with the rest of the running commentary). Every one of these
   * issues is still in `results`, still becomes a gap, and still lands red on
   * the close screen at submit.
   */
  const chipAnchors = new Map<string, ChipView[]>();
  const placed = new Set<ValidationIssue>();
  if (chips) {
    for (const question of questions) {
      for (const issue of issuesFor(question.id, section.scope)) {
        if (placed.has(issue)) continue;
        placed.add(issue);

        const chip = chipFor(doc, issue);
        if (!chip) continue;
        const existing = chipAnchors.get(question.id);
        if (existing) existing.push(chip);
        else chipAnchors.set(question.id, [chip]);
      }
    }
  }

  /*
   * Nothing to ask — every question in a catalog section gated shut by answers
   * given elsewhere. It used to say so in a sentence (D-023); a sentence about
   * the form is prose (D-033 R1), and a screen with the hand, the notes and
   * the walk on it says the same thing without one.
   */
  if (questions.length === 0) return null;

  // L4a's landing ceiling is the group's L1 quantity — the engine's number, not
  // `group.landings` read a second time here. F11a and F12a are asked at the
  // same group scope, so the same ceiling bounds their landing spans too.
  const maxLanding = derivedFor('L1', section.scope)[0]?.quantity;

  /*
   * Where the FLS header goes: in front of the FIRST §3.11 question on screen.
   *
   * Not in the inline view (D-023). Those rows exist because one answer is
   * outstanding, and a block header plus a set of camera prompts around a
   * single question would be the last look doing the section screen's job on a
   * screen the surveyor came to in order to finish.
   */
  // The first §3.11 question that has a SURFACE: F1 is derived-only and renders
  // nothing (D-033 R14), so on the Fire Life Safety section — where it is the
  // first F-question — anchoring on it would leave an empty card between the
  // hand and F2.
  const flsLeadId = only
    ? undefined
    : questions.find((q) => isFlsQuestion(q.id) && !DERIVED_ONLY_QUESTION_IDS.includes(q.id))?.id;

  return (
    <div className="qlist">
      {questions.map((question) => {
        const issues = chipAnchors.get(question.id) ?? [];
        const isNew = revealed.has(question.id);

        // D-033 R14: a derived-only question renders NOTHING, valued or not, on
        // every screen. D-029 R3 hid the empty calc, D-031 hid the valued card
        // on Building, and the round-3 brief finished the thought: a card that
        // only informs is noise. The values still compute and still travel with
        // the submission. The FLS block never anchors on one (above); a rule
        // that named one would anchor its chip on the input it compares.
        if (DERIVED_ONLY_QUESTION_IDS.includes(question.id)) return null;

        return (
          <Fragment key={`${section.id}|${question.id}`}>
            {question.id === flsLeadId ? <FlsBlock section={section} /> : null}
            <div className={isNew ? 'card qcard qcard--reveal' : 'card qcard'}>
              <QuestionField
                question={question}
                formSet={formSet}
                // D-024: the form-set filter AND any per-option gate, evaluated
                // against this document at this scope. `QuestionField` has no
                // document and must not acquire one.
                options={optionsFor(question, doc, section.scope)}
                value={answer(question.id, section.scope)}
                defaulted={isDefaultedAnswer(doc, question.id, section.scope)}
                onChange={(next: AnswerValue) => {
                  // Belt and suspenders: the control itself is `disabled` and a
                  // real pointer cannot fire this, but the provider's own
                  // `write()` refuses the same write anyway (ruling 12) — this
                  // just skips the guard's own bookkeeping for a call that was
                  // never going anywhere.
                  if (fieldsDisabled) return false;
                  const verdict = guard ? guard(question.id, next) : 'write';
                  if (verdict === 'refused') return false;
                  if (verdict === 'write') void setAnswer(question.id, section.scope, next);
                  return true;
                }}
                disabled={fieldsDisabled}
                {...(maxLanding ? { maxLanding } : {})}
              />

              {issues.map((chip) => (
                <MathChip key={chip.key} tone={chip.tone} {...(chip.why ? { why: chip.why } : {})}>
                  {chip.message}
                </MathChip>
              ))}

              {flipNote && flipAnchor === question.id ? <FlipBox>{flipNote}</FlipBox> : null}

              {/* D-033 R13: no ✎ on any card. Work that ALREADY exists on a
                * question — a note, photos — is never stranded (D-029's rule):
                * it renders in place, open, with no toggle. This is now the
                * ONLY surface that work has, which is why `bare` belongs to the
                * last look alone — see the prop. */}
              {bare ? null : (
                <QuestionWork
                  sectionInstanceId={section.id}
                  questionId={question.id}
                  questionLabel={question.label}
                />
              )}
            </div>
          </Fragment>
        );
      })}

      {flipNote && flipAnchor === undefined ? <FlipBox>{flipNote}</FlipBox> : null}
    </div>
  );
}

/** Stable empty list, so the defaults effect's dependency never churns. */
const NO_QUESTIONS: readonly QuestionDef[] = [];

/* ══════════════════════ what one chip under a question says ══════════════ */

interface ChipView {
  key: string;
  tone: MathTone;
  message: string;
  why?: string;
}

/**
 * The chip for one issue: the engine's verdict, a calm progress line, or nothing.
 *
 * Only the middle case composes anything, and it composes only a SENTENCE — the
 * counts in it come from the answers the rule compares, not from parsing the
 * engine's message. When the finding is due, the message and the explanation are
 * the engine's own words, verbatim, so the chip and the submitted gap can never
 * disagree about the same document.
 */
function chipFor(doc: SurveyDocument, issue: ValidationIssue): ChipView | undefined {
  /*
   * D-033 R4: validation speaks "only when something is actually wrong". An
   * `info` issue is not wrong — it is the engine noting that a line is priced
   * by hand at the office (§4 rule 10) — and printed under the question it is
   * a paragraph of informational text (R1). It still lands in `results` and
   * on the record; it has no chip here. Errors and warnings are unchanged.
   */
  if (issue.severity === 'info') return undefined;
  if (!heldForNow(doc, issue)) {
    return {
      key: `${issue.ruleId}:${issue.message}`,
      tone: toneForSeverity(issue.severity),
      message: issue.message,
      ...(issue.explanation ? { why: issue.explanation } : {}),
    };
  }

  // Held: nothing, until it is due (D-029 R4 — the progress sentences died).
  return undefined;
}

/* ═════════════════════════════ the shunt flip ════════════════════════════ */

export interface FlipBoxProps {
  children: ReactNode;
}

/**
 * Red, and `role="alert"`. The form has just CHANGED THE SURVEYOR'S ANSWERS —
 * that is not something to mention politely at the bottom of a queue. The text
 * is `AutoFix.explanation`, verbatim from the validation engine, so the counts
 * in it are the counts that were written.
 */
export function FlipBox({ children }: FlipBoxProps): ReactNode {
  return (
    <div className="flipbox" role="alert">
      <span aria-hidden="true">⚑</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Apply the §4 rule 5 auto-fix once, and keep its explanation on screen.
 *
 * The subtlety: applying the fix makes the issue DISAPPEAR — the whole point of
 * the rule is that it corrects itself — so the explanation cannot be rendered
 * straight off the live issue list or it would flash and vanish before anyone
 * read it. It is held until the answer that caused the flip is no longer "no",
 * which is the moment the surveyor has genuinely revisited the question.
 *
 * `appliedRef` keeps the fix from being re-applied on every render; the fix is
 * idempotent anyway (E1b becomes 0, which stops the rule firing), but a write
 * per render would queue a document push per render.
 */
function useShuntFlip(
  issue: ValidationIssue | undefined,
  readFollowUp: () => AnswerValue | undefined,
  applyFix: (fix: NonNullable<ValidationIssue['autoFix']>) => Promise<void>,
): string | null {
  const [note, setNote] = useState<string | null>(null);
  const appliedRef = useRef<string | null>(null);

  const followUp = readFollowUp();
  const stillFlipped = followUp?.kind === 'bool' && followUp.value === false;
  const fix = issue?.autoFix;
  const signature = fix ? `${fix.kind}|${JSON.stringify(fix.scope)}|${fix.explanation}` : null;

  useEffect(() => {
    if (!fix || !signature) return;
    if (appliedRef.current === signature) return;
    appliedRef.current = signature;
    setNote(fix.explanation);
    void applyFix(fix);
    // `fix` is re-created by the engine on every run; `signature` is its value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  useEffect(() => {
    if (stillFlipped) return;
    appliedRef.current = null;
    setNote(null);
  }, [stillFlipped]);

  return note;
}

/** The last of an issue's questions that is currently on screen. */
function flipAnchorFor(
  issue: ValidationIssue | undefined,
  questions: readonly QuestionDef[],
): string | undefined {
  if (!issue) return undefined;
  const onScreen = new Set(questions.map((question) => question.id));
  let anchor: string | undefined;
  for (const questionId of issue.questionIds) {
    if (onScreen.has(questionId)) anchor = questionId;
  }
  return anchor;
}

/* ═══════════════════════ per-question work, in place ═════════════════════ */

/**
 * A question's OWN note and photos — rendered only where they already exist
 * (D-033 R13, and D-029's never-strand rule).
 *
 * The ✎ that opened a drawer on every priced card is gone: the design
 * authority, 2026-09-01, on pencil and information icons — they clutter the
 * screen. The rules do not say how a NEW question-level note or photo is
 * added without it, and none is invented here — the section's notes at the
 * bottom and the hand's extras carry new work (surfaced as an open question).
 * What a surveyor has already written or photographed on a question is theirs
 * and stays in reach: the note field and the photo tiles render in place,
 * open, with no toggle and no icon. A parked photo's tile says "Needs you"
 * itself (D-025); the sentence that pointed at the ✎ went with the ✎.
 */
function QuestionWork({
  sectionInstanceId,
  questionId,
  questionLabel,
}: {
  sectionInstanceId: string;
  questionId: string;
  /** What a photo here is OF — the accessible name of the photo tiles. */
  questionLabel: string;
}): ReactNode {
  const { doc } = useSurvey();

  // The note ROW, not non-empty text: `NotesField` persists every keystroke,
  // and a surveyor clearing a note to retype it must not have the field
  // vanish under their thumb (there is no ✎ to bring it back).
  //
  // `noteFor` — the same ownership rule the field itself edits by, asked once
  // and never re-derived here. The relationship, that is, and never
  // `noteIdFor`'s derived id: that id is the convention for notes THIS app
  // mints, and a note that came down from the office, off another device or
  // out of a fixture carries whatever id it was born with (the shipped
  // St Luke's demo numbers its notes `note-1` … `note-5`). Asking for the
  // derived id stranded every one of them, and since R13 took the ✎ away there
  // is no second route: a note this check misses has no surface in the app.
  const hasNote = noteFor(doc.notes, sectionInstanceId, questionId) !== undefined;
  const hasPhotos = doc.photos.some(
    (photo) => photo.sectionInstanceId === sectionInstanceId && photo.questionId === questionId,
  );
  // Latched for the life of this visit: work that existed when the card was
  // opened keeps its surface here even after the last photo is deleted or
  // the note is emptied, so a change of mind is still possible in place.
  const [seen] = useState({ note: hasNote, photos: hasPhotos });
  const showNote = seen.note || hasNote;
  const showPhotos = seen.photos || hasPhotos;
  if (!showNote && !showPhotos) return null;

  return (
    <div className="qwork">
      {showNote ? <NotesField sectionInstanceId={sectionInstanceId} questionId={questionId} rows={2} /> : null}
      {showPhotos ? (
        <PhotoCapture
          sectionInstanceId={sectionInstanceId}
          questionId={questionId}
          label={questionLabel}
          canAdd={false}
        />
      ) : null}
    </div>
  );
}

/* ══════════════════ D-016 — the answers a screen opens on ════════════════ */

/**
 * Record the opening answer of every question on screen that has none.
 *
 * WHY IT LIVES ON THE RENDERER and not on the screen, the store, or document
 * creation: the set of questions a surveyor is actually asked is not knowable
 * ahead of time. It depends on the room type, on the form set, and on every gate
 * answered so far — M4 opens the shunt block, E7 opens the conduit legs, L9
 * opens the barricade follow-ups. `QuestionList` is the one place that knows
 * what is being asked RIGHT NOW, so it is the one place that can promise the
 * design authority's rule: *nothing on this screen is unanswered.* A gate opened
 * mid-section re-runs this on the same render that reveals its children.
 *
 * The effect is keyed on the KEYS to be written, not on the questions or the
 * document, so it fires exactly once per newly-missing answer and cannot loop:
 * the write removes those keys from the plan, the next plan is empty, and an
 * empty plan writes nothing. `pendingDefaults` refuses a sealed document and
 * refuses to overwrite, and `applyAnswerDefaults` re-checks both inside the
 * transaction.
 *
 * A failed write is swallowed here and nowhere else — `SurveyProvider` has
 * already put it on screen through `error`, and a default the surveyor never
 * asked for is not something to throw an unhandled rejection over.
 */
function useAnswerDefaults(
  doc: SurveyDocument,
  questions: readonly QuestionDef[],
  scope: Scope,
  applyDefaults: (pending: readonly PendingDefault[]) => Promise<void>,
): void {
  const pending = pendingDefaults(doc, questions, scope);
  const signature = pending.map((entry) => entry.key).join('\u0000');
  // The plan is rebuilt every render; `signature` is its value. Depending on
  // `pending` itself would re-run the effect on every render forever.
  const planRef = useRef(pending);
  planRef.current = pending;

  // What has already been sent. A write is not instant — the plan is still
  // non-empty while it is in flight — and `applyDefaults` is a fresh closure on
  // every render, so without this one missing answer would queue a second
  // document push before the first had landed.
  const sentRef = useRef('');

  useEffect(() => {
    if (planRef.current.length === 0) {
      // The write landed, or there was never anything to do. Forgetting the
      // signature here is what lets an identical plan run again later.
      sentRef.current = '';
      return;
    }
    if (sentRef.current === signature) return;
    sentRef.current = signature;
    void applyDefaults(planRef.current).catch(() => {
      // Already on screen through `SurveyProvider.error`. Releasing the mark
      // lets a later render try again instead of giving up for good.
      sentRef.current = '';
    });
  }, [signature, applyDefaults]);
}

/* ════════════════════════════ branch reveals ═════════════════════════════ */

/**
 * The ids that appeared since the previous render.
 *
 * A question that is hidden again is FORGOTTEN, so answering M4 no → yes → no →
 * yes animates the shunt block open every time rather than only the first. The
 * refs are updated during render, which is safe here because the update is
 * idempotent: a second pass (StrictMode) finds every id already known and
 * changes nothing.
 */
function useRevealed(questions: readonly QuestionDef[]): ReadonlySet<string> {
  const knownRef = useRef<Set<string> | null>(null);
  const revealedRef = useRef<Set<string>>(new Set());

  const current = new Set(questions.map((question) => question.id));

  if (knownRef.current === null) {
    // First paint of this section: nothing was "revealed", it all just arrived.
    knownRef.current = current;
  } else {
    for (const id of current) {
      if (!knownRef.current.has(id)) revealedRef.current.add(id);
    }
    for (const id of knownRef.current) {
      if (!current.has(id)) revealedRef.current.delete(id);
    }
    knownRef.current = current;
  }

  return revealedRef.current;
}
